class UdPdpCartBar extends HTMLElement {
  constructor() {
    super();

    /* ---------------------------
       SETTINGS
    ---------------------------- */
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.cutoffMonThu = this.dataset.cutoffTimeMonThuDay || '15:30';
    this.cutoffFriday = this.dataset.cutoffTimeFriday || '14:30';
    this.intervalDays = parseInt(this.dataset.intervalOfTime || "2", 10) || 1;
    this.enableCutoffTimer = this.dataset.enableCutoffTimer === 'true';

    /* ---------------------------
       STATE
    ---------------------------- */
    this.timerInterval = null;
    this.remainingMs = 0;
    this.cutoffDate = null;
    this.deliveryDate = null;
    this.deliveryDays = null;

    this.autoHideTimer = null;
    this.autoHideDelay = 15000;

    this.onCartChange = this.onCartChange.bind(this);
    this.sessionHideKey = 'ud_cart_bar_hidden';
    this.lastItemCount = null;
  }

  /* ---------------------------
     LIFECYCLE
  ---------------------------- */
  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.refreshCart();
  this.startAutoHide();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.resetCountdown();
  this.clearAutoHide();
  }

  /* ---------------------------
     DOM CACHE
  ---------------------------- */
  cacheElements() {
    this.closeButton = this.querySelector('.ud-pdp-sticky-cart-bar__close');

    this.cartBar = this.querySelector('.ud-pdp-sticky-cart-bar');
    this.progressFill = this.querySelector('.ud-progress__fill');
    this.shippingText = this.querySelector('.ud-pdp-sticky-cart-bar__shipping-text');
    this.countdownText = this.querySelector('.ud-pdp-sticky-cart-bar__coundown-text');
    this.itemText = this.querySelectorAll('.ud-cart-items');
    this.totalText = this.querySelectorAll('.ud-cart-total');
    this.shippingIcon = document.querySelector('.ud-pdp-sticky-cart-bar__shipping-box .icon')
  }

  /* ---------------------------
     EVENTS
  ---------------------------- */
  bindEvents() {
    document.addEventListener('cart:add', this.onCartChange);
    document.addEventListener('cart:update', this.onCartChange);
    document.addEventListener('quantity:update', this.onCartChange);
    document.addEventListener('discount:update', this.onCartChange);
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        sessionStorage.setItem(this.sessionHideKey, '1');
        this.clearAutoHide();
        this.hideCartBar();
      });
    }
  }

  unbindEvents() {
    document.removeEventListener('cart:add', this.onCartChange);
    document.removeEventListener('cart:update', this.onCartChange);
    document.removeEventListener('quantity:update', this.onCartChange);
    document.removeEventListener('discount:update', this.onCartChange);
  }
  startAutoHide() {
  // Do not auto-hide if user already closed it
  if (sessionStorage.getItem(this.sessionHideKey)) return;

  this.clearAutoHide();
    this.autoHideTimer = setTimeout(() => {
      this.hideCartBar();
    }, this.autoHideDelay);
  }

  clearAutoHide() {
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer);
      this.autoHideTimer = null;
    }
  }

  hideCartBar() {
    this.cartBar?.classList.add('hidden');
    this.resetCountdown();
  }

  showCartBar() {
    this.cartBar?.classList.remove('hidden');
    this.startAutoHide();
  }


  onCartChange() {
    this.refreshCart();
  }

  /* ---------------------------
     FORMATTERS
  ---------------------------- */
  /**
   * @param {number} cents
   */
  formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: Shopify.currency.active,
    });
  }
  getNowInUK() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    const map = {};
    parts.forEach(p => {
      if (p.type !== 'literal') map[p.type] = p.value;
    });

    return new Date(
      `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`
    );
  }

  formatCountdown(ms) {
    if (ms <= 0) return '00:00:00';

    const totalSeconds = Math.floor(ms / 1000);
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    const s = String(totalSeconds % 60).padStart(2, '0');

    return `${h}:${m}:${s}`;
  }

  formatDeliveryDate(date) {
    if (!(date instanceof Date)) return '';

    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'long' });

    const suffix =
      day > 3 && day < 21 ? 'th' :
        day % 10 === 1 ? 'st' :
          day % 10 === 2 ? 'nd' :
            day % 10 === 3 ? 'rd' : 'th';

    return `${day}${suffix} ${month}`;
  }
  // @ts-ignore
  formatDeliveryDays(days) {
    const deliveryDate = this.deliveryDate;
    if (days <= 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    // @ts-ignore
    return deliveryDate.toLocaleDateString('en-GB', {
      weekday: 'long'
    });
  }

  addWorkingDays(startDate, days) {
    const d = new Date(startDate);
    let added = 0;

    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();

      // Skip Saturday (6) and Sunday (0)
      if (day !== 6 && day !== 0) {
        added++;
      }
    }

    return d;
  }


  /* ---------------------------
     DATE CALCULATION (CORE LOGIC)
  ---------------------------- */
  calculateDeliveryDate() {
    // const now = new Date();
    const now = this.getNowInUK();
    const day = now.getDay();
    const minutesNow = now.getHours() * 60 + now.getMinutes();

    const parseMinutes = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    const monThuCutoff = parseMinutes(this.cutoffMonThu);
    const friCutoff = parseMinutes(this.cutoffFriday);

    const cutoffForDay = (d) => d === 5 ? friCutoff : monThuCutoff;

    const withTime = (base, mins) => {
      const d = new Date(base);
      d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
      return d;
    };

    const nextWorkingDay = (d) => {
      const n = new Date(d);
      if (n.getDay() === 5) n.setDate(n.getDate() + 3);
      else if (n.getDay() === 6) n.setDate(n.getDate() + 2);
      else if (n.getDay() === 0) n.setDate(n.getDate() + 1);
      else n.setDate(n.getDate() + 1);
      return n;
    };

    let cutoffDate;

    if (day === 6 || day === 0) {
      const monday = nextWorkingDay(now);
      cutoffDate = withTime(monday, monThuCutoff);
    } else {
      const todayCutoff = cutoffForDay(day);

      if (minutesNow >= todayCutoff) {
        const nextDay = nextWorkingDay(now);
        cutoffDate = withTime(nextDay, cutoffForDay(nextDay.getDay()));
      } else {
        cutoffDate = withTime(now, todayCutoff);
      }
    }

    // const deliveryDate = new Date(cutoffDate);
    // deliveryDate.setDate(deliveryDate.getDate() + this.intervalDays);
    const deliveryDate = this.addWorkingDays(
      cutoffDate,
      this.intervalDays
    );

    return { cutoffDate, deliveryDate };
  }

  calculateDeliveryDays() {
    const now = this.getNowInUK();

    // Strip time to avoid partial-day issues
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(
      // @ts-ignore
      this.deliveryDate.getFullYear(),
      // @ts-ignore
      this.deliveryDate.getMonth(),
      // @ts-ignore
      this.deliveryDate.getDate()
    );

    // @ts-ignore
    const diffMs = end - start;
    this.deliveryDays = Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
  }
  /* ---------------------------
     COUNTDOWN
  ---------------------------- */

  startCountdown() {
    if (this.timerInterval) return;

    const { cutoffDate, deliveryDate } = this.calculateDeliveryDate();
    this.cutoffDate = cutoffDate;
    this.deliveryDate = deliveryDate;
    this.calculateDeliveryDays();

    this.timerInterval = setInterval(() => {
      this.remainingMs = this.cutoffDate.getTime() - this.getNowInUK();

      if (this.remainingMs <= 0) {
        this.resetCountdown();
        return;
      }

       this.renderCountdown();
    }, 1000);
  }

  resetCountdown() {
    clearInterval(this.timerInterval);
    this.timerInterval = null;
    this.remainingMs = 0;
  }

  renderCountdown() {

    if (this.enableCutoffTimer) {
      if(this.countdownText)this.countdownText.innerHTML =
        `Complete transaction in <strong>${this.formatCountdown(this.remainingMs)}</strong>
       hr to receive by <strong>${this.formatDeliveryDays(this.deliveryDays)}</strong>.`;
    }

  }

  /* ---------------------------
     CART LOGIC
  ---------------------------- */
  async refreshCart() {
    const res = await fetch('/cart.js');
    const cart = await res.json();

    // UKD: a dismissal only lasts until the customer adds something else.
    // If the item count went UP since we last looked, clear the dismissal
    // so the bar re-appears. First load (lastItemCount === null) does not count.
    const currentCount = cart ? cart.item_count : 0;
    if (this.lastItemCount !== null && currentCount > this.lastItemCount) {
      sessionStorage.removeItem(this.sessionHideKey);
    }
    this.lastItemCount = currentCount;

    // Session-based hide check
    if (sessionStorage.getItem(this.sessionHideKey) === '1') {
      this.hideCartBar();
      return;
    }
    // Empty cart check
    if (!cart || cart.item_count === 0) {
      this.hideCartBar();
      return;
    }
    this.showCartBar();

    const remaining = this.threshold - cart.total_price;
    const progress = Math.min((cart.total_price / this.threshold) * 100, 100);
    if (this.progressFill) this.progressFill.style.width = `${progress}%`;

    if (remaining > 0) {
      if (this.shippingText) this.shippingText.innerHTML =
        `Spend <strong>${this.formatMoney(remaining)}</strong> more to <strong>Get FREE</strong> next working day`;
      if (this.countdownText) this.countdownText.innerHTML = '';
      if (this.shippingIcon) this.shippingIcon.style.display = 'block'
      this.startCountdown();
    } else {
      if(this.shippingText)this.shippingText.innerHTML =
      `<strong>Congratulations!</strong> You've unlocked FREE next working day delivery!`;
      this.startCountdown();
      if (this.shippingIcon) this.shippingIcon.style.display = 'none'
    }

    this.itemText?.forEach((el) => {
      el.innerHTML =
        `Total item${cart.item_count > 1 ? 's' : ''} <strong>${cart.item_count}</strong>`;
    });

    this.totalText?.forEach((el) => {
      el.innerHTML =
        `Total price <strong>${this.formatMoney(cart.total_price)}</strong>`;
    });
  }
}

customElements.define('ud-pdp-sticky-cart-bar', UdPdpCartBar);